/**
 * StartAttendanceStep Component
 * Step 3 of Quick Attendance Wizard - Summary and start attendance
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  MedicalServices as ExamIcon,
  ConfirmationNumber as QueueIcon,
  CheckCircle as CheckIcon,
  Favorite as HeartIcon,
} from '@mui/icons-material';
import { StartAttendanceStepProps } from '../QuickAttendanceWizard.types';
import {
  hasTriageData,
  formatBloodPressure,
  calculateBMI,
} from '../../../../../types/triage';

/**
 * StartAttendanceStep - Final step of the wizard
 * Shows summary and initiates the attendance
 */
export const StartAttendanceStep: React.FC<StartAttendanceStepProps> = ({
  appointment,
  triageData,
  queueNumber,
  checkInPerformed,
}) => {
  const hasTriage = hasTriageData(triageData);
  const displayQueueNumber = queueNumber ?? appointment.queueNumber;
  const bmi = calculateBMI(triageData.weight, triageData.height);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Revise as informações antes de iniciar o atendimento:
      </Typography>
      {/* Patient Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PersonIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            {appointment.employeeName}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {appointment.companyName}
              </Typography>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <ExamIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {appointment.examType}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {displayQueueNumber && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={2}
            p={2}
            bgcolor="primary.light"
            borderRadius={1}
          >
            <QueueIcon sx={{ mr: 1, color: 'primary.contrastText' }} />
            <Typography variant="h4" fontWeight="bold" color="primary.contrastText">
              Senha: {String(displayQueueNumber).padStart(3, '0')}
            </Typography>
          </Box>
        )}
      </Paper>
      {/* Triage Summary */}
      {hasTriage && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <HeartIcon color="error" />
            <Typography variant="subtitle2" fontWeight="bold">
              Triagem Realizada
            </Typography>
          </Box>

          <Grid container spacing={1}>
            {(triageData.bloodPressureSystolic || triageData.bloodPressureDiastolic) && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  PA
                </Typography>
                <Typography variant="body2">
                  {formatBloodPressure(
                    triageData.bloodPressureSystolic,
                    triageData.bloodPressureDiastolic
                  )}
                </Typography>
              </Grid>
            )}
            {triageData.heartRate && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  FC
                </Typography>
                <Typography variant="body2">{triageData.heartRate} bpm</Typography>
              </Grid>
            )}
            {triageData.temperature && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  Temp
                </Typography>
                <Typography variant="body2">{triageData.temperature} °C</Typography>
              </Grid>
            )}
            {triageData.oxygenSaturation && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  SpO2
                </Typography>
                <Typography variant="body2">{triageData.oxygenSaturation}%</Typography>
              </Grid>
            )}
            {triageData.weight && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  Peso
                </Typography>
                <Typography variant="body2">{triageData.weight} kg</Typography>
              </Grid>
            )}
            {triageData.height && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  Altura
                </Typography>
                <Typography variant="body2">{triageData.height} cm</Typography>
              </Grid>
            )}
            {bmi && (
              <Grid
                size={{
                  xs: 6,
                  sm: 4
                }}>
                <Typography variant="caption" color="text.secondary">
                  IMC
                </Typography>
                <Typography variant="body2">{bmi}</Typography>
              </Grid>
            )}
          </Grid>

          {triageData.observations && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Observacoes
              </Typography>
              <Typography variant="body2">{triageData.observations}</Typography>
            </>
          )}
        </Paper>
      )}
      {/* Status Chips */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        {checkInPerformed && (
          <Chip
            icon={<CheckIcon />}
            label="Check-in realizado"
            color="success"
            size="small"
          />
        )}
        {hasTriage && (
          <Chip
            icon={<HeartIcon />}
            label="Triagem registrada"
            color="info"
            size="small"
          />
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Alert severity="warning">
        Ao clicar em <strong>Iniciar Atendimento</strong>, o status do paciente sera
        alterado para <strong>Em Atendimento</strong>.
      </Alert>
    </Box>
  );
};

export default StartAttendanceStep;
